// Climate fields + biome selection (GEN-3). Five independent low-frequency noise
// fields; biome is a label chosen from them by the ordered placement rules
// (first match wins — rules are authored most-specific first). Height (terrain.ts)
// reads the SAME continuous fields, so borders never seam.
import { Simplex, fbm2 } from '../../engine/noise';
import { CLIMATE } from '../../config';
import type { PlacementRule } from '../../registry/schemas';

export interface Climate {
  temperature: number;
  humidity: number;
  continentalness: number;
  erosion: number;
  ridges: number;
}

export class ClimateSampler {
  private readonly t: Simplex;
  private readonly h: Simplex;
  private readonly c: Simplex;
  private readonly e: Simplex;
  private readonly r: Simplex;

  constructor(seed: number | string | bigint) {
    this.t = new Simplex(`${seed}#temperature`);
    this.h = new Simplex(`${seed}#humidity`);
    this.c = new Simplex(`${seed}#continental`);
    this.e = new Simplex(`${seed}#erosion`);
    this.r = new Simplex(`${seed}#ridges`);
  }

  sample(x: number, z: number): Climate {
    return {
      temperature: fbm2(this.t, x * CLIMATE.TEMPERATURE_FREQ, z * CLIMATE.TEMPERATURE_FREQ, { octaves: 2 }),
      humidity: fbm2(this.h, x * CLIMATE.HUMIDITY_FREQ, z * CLIMATE.HUMIDITY_FREQ, { octaves: 2 }),
      continentalness: fbm2(this.c, x * CLIMATE.CONTINENTAL_FREQ, z * CLIMATE.CONTINENTAL_FREQ, { octaves: 3 }),
      erosion: fbm2(this.e, x * CLIMATE.EROSION_FREQ, z * CLIMATE.EROSION_FREQ, { octaves: 2 }),
      ridges: fbm2(this.r, x * CLIMATE.RIDGE_FREQ, z * CLIMATE.RIDGE_FREQ, { octaves: 2 }),
    };
  }
}

const inRange = (v: number, r?: readonly [number, number]): boolean => !r || (v >= r[0] && v <= r[1]);

export function selectBiome(c: Climate, rules: readonly PlacementRule[]): string {
  for (const rule of rules) {
    if (
      inRange(c.temperature, rule.temperature) &&
      inRange(c.humidity, rule.humidity) &&
      inRange(c.continentalness, rule.continentalness) &&
      inRange(c.erosion, rule.erosion) &&
      inRange(c.ridges, rule.ridges)
    ) {
      return rule.biome;
    }
  }
  return rules[rules.length - 1]?.biome ?? 'plains';
}
